import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AUTH_SESSION_CHANGE_EVENT } from '../../../utils/authSession';
import { useBoardChatCable } from '../hooks/useBoardChatCable';
import {
  fetchChatBadges,
  fetchChatSession,
  fetchMyChatTeams,
} from '../services/boardChatService';
import { playBoardChatNewMessageSound, armBoardChatNotifySound } from '../utils/boardChatNotifySound';
import {
  armBoardChatDesktopNotify,
  showBoardChatDesktopNotification,
} from '../utils/boardChatDesktopNotify';

const BoardChatRealtimeContext = createContext(null);

const RECONCILE_MS = 45000;

function hasAuthToken() {
  try {
    return Boolean(localStorage.getItem('authToken'));
  } catch {
    return false;
  }
}

/**
 * Sessão Chatwoot + RoomChannel + badges (lidos em toda a sessão autenticada).
 */
export function BoardChatRealtimeProvider({ children }) {
  const [status, setStatus] = useState('idle'); // idle | loading | ready | denied
  const [session, setSession] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unattendedCount, setUnattendedCount] = useState(0);
  const [inboxCount, setInboxCount] = useState(0);
  const [preAtendimentoUnreadCount, setPreAtendimentoUnreadCount] = useState(0);
  const [preAtendimentoUnattendedCount, setPreAtendimentoUnattendedCount] = useState(0);
  const [preAtendimentoInboxCount, setPreAtendimentoInboxCount] = useState(0);
  const [preAtendimentoPendingOsCount, setPreAtendimentoPendingOsCount] = useState(0);
  const [preAtendimentoTeamId, setPreAtendimentoTeamId] = useState(null);
  const preAtendimentoTeamIdRef = useRef(null);
  preAtendimentoTeamIdRef.current = preAtendimentoTeamId;
  const teamIdsRef = useRef([]);
  const sessionRef = useRef(null);
  const reconcileTimerRef = useRef(null);
  /** Conversa aberta no painel (thread visível) — evita badge/unread fantasma ao vivo. */
  const viewingConversationIdRef = useRef(null);
  sessionRef.current = session;

  const setViewingConversationId = useCallback((conversationId) => {
    const n = conversationId != null ? Number(conversationId) : null;
    viewingConversationIdRef.current =
      Number.isFinite(n) && n > 0 ? n : null;
  }, []);

  const clearState = useCallback(() => {
    setStatus(hasAuthToken() ? 'denied' : 'idle');
    setSession(null);
    setUnreadCount(0);
    setUnattendedCount(0);
    setInboxCount(0);
    setPreAtendimentoUnreadCount(0);
    setPreAtendimentoUnattendedCount(0);
    setPreAtendimentoInboxCount(0);
    setPreAtendimentoPendingOsCount(0);
    setPreAtendimentoTeamId(null);
    teamIdsRef.current = [];
    viewingConversationIdRef.current = null;
  }, []);

  const refreshBadges = useCallback(async () => {
    if (!hasAuthToken()) return;
    try {
      const res = await fetchChatBadges();
      const d = res.data || {};
      setUnreadCount(Number(d.unreadCount) || 0);
      setUnattendedCount(Number(d.unattendedCount) || 0);
      setInboxCount(Number(d.inboxCount) || 0);
      setPreAtendimentoUnreadCount(Number(d.preAtendimentoUnreadCount) || 0);
      setPreAtendimentoUnattendedCount(Number(d.preAtendimentoUnattendedCount) || 0);
      setPreAtendimentoInboxCount(Number(d.preAtendimentoInboxCount) || 0);
      setPreAtendimentoPendingOsCount(Number(d.preAtendimentoPendingOsCount) || 0);
      if (d.preAtendimentoTeamId != null) {
        setPreAtendimentoTeamId(Number(d.preAtendimentoTeamId));
      }
    } catch {
      /* silencioso */
    }
  }, []);

  const scheduleRefreshBadges = useCallback(() => {
    if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
    reconcileTimerRef.current = setTimeout(() => {
      reconcileTimerRef.current = null;
      refreshBadges();
    }, 350);
  }, [refreshBadges]);

  const loadSession = useCallback(async () => {
    if (!hasAuthToken()) {
      setStatus('idle');
      setSession(null);
      setUnreadCount(0);
      setUnattendedCount(0);
      setInboxCount(0);
      teamIdsRef.current = [];
      return;
    }
    setStatus((s) => (s === 'ready' ? 'ready' : 'loading'));
    try {
      const res = await fetchChatSession();
      if (!res.data?.hasAccess) {
        setSession(null);
        setUnreadCount(0);
        setUnattendedCount(0);
        setInboxCount(0);
        setPreAtendimentoUnreadCount(0);
        setPreAtendimentoUnattendedCount(0);
        setPreAtendimentoInboxCount(0);
        setPreAtendimentoPendingOsCount(0);
        setPreAtendimentoTeamId(null);
        teamIdsRef.current = [];
        setStatus('denied');
        return;
      }
      setSession(res.data);
      try {
        const teamsRes = await fetchMyChatTeams();
        const list = teamsRes.data || [];
        const arr = Array.isArray(list) ? list : [];
        teamIdsRef.current = arr
          .map((t) => Number(t.id ?? t.team_id))
          .filter((id) => Number.isFinite(id) && id > 0);
      } catch {
        teamIdsRef.current = [];
      }
      setStatus('ready');
      await refreshBadges();
    } catch {
      setSession(null);
      setUnreadCount(0);
      setUnattendedCount(0);
      setInboxCount(0);
      setPreAtendimentoUnreadCount(0);
      setPreAtendimentoUnattendedCount(0);
      setPreAtendimentoInboxCount(0);
      setPreAtendimentoPendingOsCount(0);
      setPreAtendimentoTeamId(null);
      teamIdsRef.current = [];
      setStatus(hasAuthToken() ? 'denied' : 'idle');
    }
  }, [refreshBadges]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  useEffect(() => {
    return () => {
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
    };
  }, []);

  const hasAccess = status === 'ready';

  useEffect(() => {
    if (!hasAccess) return;
    armBoardChatNotifySound();
    armBoardChatDesktopNotify();
  }, [hasAccess]);

  useEffect(() => {
    if (!hasAccess) return undefined;
    const id = setInterval(() => {
      refreshBadges();
    }, RECONCILE_MS);
    return () => clearInterval(id);
  }, [hasAccess, refreshBadges]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (!hasAuthToken()) {
        clearState();
        return;
      }
      // Sem sessão e sem acesso: não retentar (evita loop 403)
      if (!sessionRef.current) {
        if (status !== 'denied') loadSession();
        return;
      }
      refreshBadges();
    };
    const onFocus = () => {
      if (!hasAuthToken()) return;
      if (sessionRef.current) refreshBadges();
      else if (status !== 'denied') loadSession();
    };
    const onStorage = (e) => {
      if (e.key !== 'authToken') return;
      if (e.newValue) loadSession();
      else clearState();
    };
    // Login/logout na mesma aba (storage não dispara) — retenta mesmo após denied
    const onAuthSessionChange = (e) => {
      if (e?.detail?.active) loadSession();
      else clearState();
    };
    const pollId = setInterval(() => {
      const tok = hasAuthToken();
      if (!tok && sessionRef.current) {
        clearState();
        return;
      }
      // Não retentar quando já sabemos que não há acesso ao chat
      if (tok && !sessionRef.current && status !== 'loading' && status !== 'denied') {
        loadSession();
      }
    }, 4000);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('storage', onStorage);
    window.addEventListener(AUTH_SESSION_CHANGE_EVENT, onAuthSessionChange);
    return () => {
      clearInterval(pollId);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(AUTH_SESSION_CHANGE_EVENT, onAuthSessionChange);
    };
  }, [clearState, loadSession, refreshBadges, status]);

  const teamBelongs = useCallback((teamId) => {
    if (teamId == null) return true; // payload sem team → deixa API reconciliar
    if (!teamIdsRef.current.length) return true;
    return teamIdsRef.current.includes(Number(teamId));
  }, []);

  const handleMessageCreated = useCallback(
    (msg) => {
      if (!msg?.isIncoming) return;
      if (!teamBelongs(msg.teamId)) return;
      const viewingId = viewingConversationIdRef.current;
      const msgConvId =
        msg.conversationId != null ? Number(msg.conversationId) : null;
      const isViewingThread =
        viewingId != null &&
        msgConvId != null &&
        viewingId === msgConvId &&
        typeof document !== 'undefined' &&
        document.visibilityState === 'visible';
      // Já dentro da conversa ao vivo: painel marca lida + refreshBadges
      if (isViewingThread) return;
      // Conta mensagem não lida na hora; GET /badges reconcilia em seguida
      const isPreTeam =
        preAtendimentoTeamIdRef.current != null &&
        msg.teamId != null &&
        Number(msg.teamId) === Number(preAtendimentoTeamIdRef.current);
      if (isPreTeam) {
        setPreAtendimentoUnreadCount((n) => n + 1);
      } else {
        setUnreadCount((n) => n + 1);
      }
      playBoardChatNewMessageSound();
      showBoardChatDesktopNotification(msg);
      scheduleRefreshBadges();
    },
    [scheduleRefreshBadges, teamBelongs]
  );

  const handleConversationPatch = useCallback(
    (patch) => {
      if (patch?.unread_count == null) return;
      if (!teamBelongs(patch.teamId)) return;
      const viewingId = viewingConversationIdRef.current;
      if (
        viewingId != null &&
        Number(patch.id) === viewingId &&
        typeof document !== 'undefined' &&
        document.visibilityState === 'visible'
      ) {
        // Thread aberta: mark-read no painel; evita badge fantasma no reconcile
        return;
      }
      scheduleRefreshBadges();
    },
    [scheduleRefreshBadges, teamBelongs]
  );

  useBoardChatCable(
    session,
    scheduleRefreshBadges,
    hasAccess && Boolean(session?.pubsubToken) && Boolean(session?.wsBaseUrl),
    null,
    handleConversationPatch,
    handleMessageCreated
  );

  const value = useMemo(
    () => ({
      status,
      hasAccess,
      session,
      unreadCount,
      unattendedCount,
      inboxCount,
      preAtendimentoUnreadCount,
      preAtendimentoUnattendedCount,
      preAtendimentoInboxCount,
      preAtendimentoPendingOsCount,
      totalUnreadCount: unreadCount + preAtendimentoUnreadCount,
      totalInboxCount: inboxCount + preAtendimentoInboxCount,
      preAtendimentoTeamId,
      refreshBadges,
      reloadSession: loadSession,
      setViewingConversationId,
    }),
    [
      status,
      hasAccess,
      session,
      unreadCount,
      unattendedCount,
      inboxCount,
      preAtendimentoUnreadCount,
      preAtendimentoUnattendedCount,
      preAtendimentoInboxCount,
      preAtendimentoPendingOsCount,
      preAtendimentoTeamId,
      refreshBadges,
      loadSession,
      setViewingConversationId,
    ]
  );

  return (
    <BoardChatRealtimeContext.Provider value={value}>
      {children}
    </BoardChatRealtimeContext.Provider>
  );
}

export function useBoardChatRealtime() {
  return useContext(BoardChatRealtimeContext);
}
